import { useState, useRef, useEffect } from "react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/authContext";
import {
    X,
    Camera,
    Loader2,
    Save,
    Clock,
    Info,
    Package,
    Plus,
    Shield,
    Eye,
    EyeOff,
    Phone,
    Check,
    Trash2
} from "lucide-react";
import { CategoryIcon } from "./CategoryIcon";
import { ImageCaptureInput } from "@/components/ImageCaptureInput";
import { ImageCropperModal } from "@/components/ImageCropperModal";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CATEGORIES } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { getAuthHeader } from "@/lib/authHelpers";
import { useI18n } from "@/lib/i18n";

interface EditShopModalProps {
    shop: any;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onShopUpdated?: () => void;
    mode?: "full" | "address-only" | "community-edit";
}

interface ImageItem {
    url: string;
    isNew?: boolean;
}

export function EditShopModal({ shop, open, onOpenChange, onShopUpdated, mode = "full" }: EditShopModalProps) {
    const { toast } = useToast();
    const { user } = useAuth();
    const { t } = useI18n();
    const queryClient = useQueryClient();

    const isOwnerOrAdmin = user?.role === 'admin' || (user?.id && shop.claimed_by_user_id === user.id);
    const isVerified = shop.verification_status === 'verified' || shop.is_verified || user?.role === 'admin';
    const canEditBanner = isOwnerOrAdmin && isVerified;
    const maxImages = isOwnerOrAdmin ? 12 : 4;

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: typeof shop.name === 'string' ? shop.name : (shop.name as any)?.value || "",
        address: typeof shop.address === 'string' ? shop.address : (shop.address as any)?.value || "",
        description: shop.description || "",
        website: typeof shop.website === 'string' ? shop.website : (shop.website as any)?.value || "",
        has_charity: !!(shop.has_charity ?? true),
        has_horeca: !!(shop.has_horeca ?? false),
        has_card_payment: !!(shop.has_card_payment ?? false),
        has_cash_payment: !!(shop.has_cash_payment ?? false),
        categories: shop.categories || [],
        phone_number: shop.phone_number || "",
        postal_code: shop.postal_code || "",
        city: typeof shop.city === 'string' ? shop.city : (shop.city as any)?.value || "",
        country: typeof shop.country === 'string' ? shop.country : (shop.country as any)?.value || "Nederland",
        is_temporarily_closed: !!shop.is_temporarily_closed,
        temporarily_closed_until: shop.temporarily_closed_until ? new Date(shop.temporarily_closed_until).toISOString().split('T')[0] : "",
        temporarily_closed_reason: shop.temporarily_closed_reason || ""
    });

    const [bannerImage, setBannerImage] = useState<ImageItem | null>(null);
    const [facadeImage, setFacadeImage] = useState<ImageItem | null>(null);
    const [openingSchedule, setOpeningSchedule] = useState<{ weekday: number, open: string, close: string, is_closed: boolean }[]>([]);
    const [images, setImages] = useState<ImageItem[]>([]);
    const [pendingImages, setPendingImages] = useState<string[]>([]);
    const [croppingIndex, setCroppingIndex] = useState(-1);
    const [croppingTarget, setCroppingTarget] = useState<"banner" | "facade" | "gallery" | null>(null);

    const { data: suggestions, refetch: refetchSuggestions } = useQuery<any[]>({
        queryKey: ["shop-suggestions", shop.id],
        queryFn: async () => {
            const authHeader = await getAuthHeader();
            const res = await fetch(`/api/shops/${shop.id}/suggestions`, {
                headers: authHeader || {}
            });
            if (!res.ok) return [];
            return res.json();
        },
        enabled: Boolean(open && shop.id && isOwnerOrAdmin)
    });

    const handleApprove = async (suggestionId: string) => {
        try {
            const authHeader = await getAuthHeader();
            const res = await fetch(`/api/shops/${shop.id}/suggestions/${suggestionId}/approve`, {
                method: "POST",
                headers: authHeader || {}
            });
            if (!res.ok) throw new Error(t("editShop.approveFailed"));
            toast({ title: t("editShop.approved"), description: t("editShop.suggestionApplied") });
            refetchSuggestions();
            queryClient.invalidateQueries({ queryKey: ["shop", shop.id] });
        } catch (error: any) {
            toast({ title: t("common.error"), description: error.message, variant: "destructive" });
        }
    };

    const handleReject = async (suggestionId: string) => {
        try {
            const authHeader = await getAuthHeader();
            const res = await fetch(`/api/shops/${shop.id}/suggestions/${suggestionId}`, {
                method: "DELETE",
                headers: authHeader || {}
            });
            if (!res.ok) throw new Error(t("routeList.deleteFailed"));
            toast({ title: t("editShop.removed"), description: t("editShop.suggestionRejected") });
            refetchSuggestions();
        } catch (error: any) {
            toast({ title: t("common.error"), description: error.message, variant: "destructive" });
        }
    };

    // Initialize state from shop data
    useEffect(() => {
        if (open && shop.id) {
            // Photos
            const initialImages: ImageItem[] = [];
            if (shop.profileImageUrl) initialImages.push({ url: shop.profileImageUrl });

            // Check structured fields first
            const photos = Array.isArray(shop.photos) ? shop.photos : [];
            const existingFacade = photos.find((p: any) => !!p?.is_facade);
            setFacadeImage(existingFacade?.image_url ? { url: existingFacade.image_url } : null);

            const photoUrls = shop.fields?.photos?.value?.urls || photos.map((p: any) => p.image_url) || [];
            if (Array.isArray(photoUrls)) {
                photoUrls.forEach((url: string) => {
                    if (existingFacade?.image_url && url === existingFacade.image_url) return;
                    if (url && !initialImages.find(img => img.url === url)) {
                        initialImages.push({ url });
                    }
                });
            }
            setImages(initialImages);

            // Banner
            setBannerImage(shop.bannerUrl ? { url: shop.bannerUrl } : null);

            // Opening Hours
            const schedule = [];
            for (let i = 0; i < 7; i++) {
                const oh = (shop.openingHoursStructured || []).find((h: any) => h.weekday === i);
                schedule.push({
                    weekday: i,
                    open: oh?.open_time?.slice(0, 5) || "09:00",
                    close: oh?.close_time?.slice(0, 5) || "17:00",
                    is_closed: oh?.is_closed ?? true
                });
            }
            setOpeningSchedule(schedule);

            // Basic Info
            setFormData({
                name: typeof shop.name === 'string' ? shop.name : (shop.name as any)?.value || "",
                address: typeof shop.address === 'string' ? shop.address : (shop.address as any)?.value || "",
                description: shop.description || "",
                website: typeof shop.website === 'string' ? shop.website : (shop.website as any)?.value || "",
                has_charity: !!(shop.has_charity ?? true),
                has_horeca: !!(shop.has_horeca ?? false),
                has_card_payment: !!(shop.has_card_payment ?? false),
                has_cash_payment: !!(shop.has_cash_payment ?? false),
                categories: shop.categories || [],
                phone_number: shop.phone_number || "",
                postal_code: shop.postal_code || "",
                city: typeof shop.city === 'string' ? shop.city : (shop.city as any)?.value || "",
                country: typeof shop.country === 'string' ? shop.country : (shop.country as any)?.value || "Nederland",
                is_temporarily_closed: !!shop.is_temporarily_closed,
                temporarily_closed_until: shop.temporarily_closed_until ? new Date(shop.temporarily_closed_until).toISOString().split('T')[0] : "",
                temporarily_closed_reason: shop.temporarily_closed_reason || ""
            });
        }
    }, [open, shop]);

    const toggleCategory = (cat: string) => {
        setFormData(prev => ({
            ...prev,
            categories: prev.categories.includes(cat)
                ? prev.categories.filter((c: string) => c !== cat)
                : [...prev.categories, cat]
        }));
    };

    const removeImage = (index: number) => {
        setImages(images.filter((_, i) => i !== index));
    };

    const removeBanner = () => setBannerImage(null);

    const updateSchedule = (idx: number, field: keyof typeof openingSchedule[0], value: any) => {
        const newSchedule = [...openingSchedule];
        newSchedule[idx] = { ...newSchedule[idx], [field]: value };
        setOpeningSchedule(newSchedule);
    };

    const extractApiError = async (res: Response, fallback: string): Promise<string> => {
        const payload = await res.json().catch(() => null as any);
        const baseMessage = payload?.error || payload?.details || fallback;
        const requestId = payload?.requestId ? ` [requestId: ${payload.requestId}]` : "";
        return `${baseMessage}${requestId}`;
    };

    const uploadImages = async (
        imagesToUpload: string[],
        authHeader: Record<string, string>
    ): Promise<string[]> => {
        if (imagesToUpload.length === 0) return [];
        const uploadRes = await fetch("/api/upload", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...authHeader
            },
            body: JSON.stringify({
                images: imagesToUpload,
                bucket: "shop-photos"
            })
        });
        if (!uploadRes.ok) {
            const message = await extractApiError(uploadRes, t("addShop.photoUploadFailed"));
            throw new Error(message);
        }
        const payload = await uploadRes.json().catch(() => null as any);
        return Array.isArray(payload?.urls) ? payload.urls : [];
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const authHeader = await getAuthHeader();
            if (!authHeader) throw new Error(t("account.error.notAuthenticated"));

            // 1a. Upload Banner if new
            let bannerUrl = bannerImage?.url || null;
            if (canEditBanner && bannerImage?.isNew) {
                const urls = await uploadImages([bannerImage.url], authHeader);
                bannerUrl = urls[0];
            }

            // 1b. Upload facade image if new
            let facadeImageUrl = facadeImage?.url || null;
            if (facadeImage?.isNew) {
                const urls = await uploadImages([facadeImage.url], authHeader);
                facadeImageUrl = urls?.[0] || null;
            }

            // 1c. Upload new images
            const newImagesToUpload = images.filter(img => img.isNew);
            let uploadedUrls: string[] = [];

            if (newImagesToUpload.length > 0) {
                uploadedUrls = await uploadImages(newImagesToUpload.map(img => img.url), authHeader);
            }

            const nonFacadeImageUrls = [
                ...images.filter(img => !img.isNew).map(img => img.url),
                ...uploadedUrls
            ];
            const finalImageUrls = facadeImageUrl
                ? [facadeImageUrl, ...nonFacadeImageUrls.filter((url) => url !== facadeImageUrl)]
                : nonFacadeImageUrls;

            const openingHoursPayload = openingSchedule.map(s => ({
                weekday: s.weekday,
                open_time: s.is_closed ? null : s.open,
                close_time: s.is_closed ? null : s.close,
                is_closed: s.is_closed
            }));

            const res = await fetch(`/api/shops/${shop.id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    ...authHeader
                },
                body: JSON.stringify({
                    ...formData,
                    imageUrls: finalImageUrls,
                    facadeImageUrl,
                    ...(canEditBanner ? { bannerUrl } : {}),
                    openingHours: openingHoursPayload,
                    is_temporarily_closed: formData.is_temporarily_closed,
                    temporarily_closed_until: formData.temporarily_closed_until ? new Date(formData.temporarily_closed_until).toISOString() : null,
                    temporarily_closed_reason: formData.temporarily_closed_reason || null
                })
            });
            if (!res.ok) {
                const message = await extractApiError(res, t("editShop.saveFailed"));
                throw new Error(message);
            }

            toast({
                title: isOwnerOrAdmin ? t("editShop.updated") : t("editShop.suggestionSaved"),
                description: isOwnerOrAdmin ? t("editShop.shopSaved") : t("editShop.suggestionSavedDesc")
            });

            queryClient.invalidateQueries({ queryKey: ["claimed-shops"] });
            queryClient.invalidateQueries({ queryKey: ["shop", shop.id] });
            if (onShopUpdated) onShopUpdated();
            onOpenChange(false);
        } catch (error: any) {
            console.error("[EditShopModal] Save failed", {
                shopId: shop?.id,
                message: error?.message,
            });
            toast({
                title: t("common.error"),
                description: error.message || t("editShop.saveFailed"),
                variant: "destructive"
            });
        } finally {
            setLoading(false);
        }
    };

    const renderSuggestions = () => {
        if (!suggestions || suggestions.length === 0) {
            return (
                <div className="text-center py-12 text-muted-foreground bg-slate-50 rounded-xl border border-dashed">
                    <Info className="h-8 w-8 mx-auto mb-2 opacity-20" />
                    <p className="text-sm">{t("editShop.noPendingSuggestions")}</p>
                </div>
            );
        }

        return (
            <div className="space-y-3">
                <div className="bg-primary/5 border border-primary/10 rounded-lg p-3 text-xs text-primary mb-2 flex items-start gap-2">
                    <Shield className="h-4 w-4 shrink-0" />
                    <p>{t("editShop.communitySuggestionsDesc")}</p>
                </div>
                {suggestions.map((s: any) => (
                    <div key={s.id} className="border rounded-lg p-4 space-y-3 bg-white shadow-sm border-slate-200">
                        <div className="flex justify-between items-start">
                            <div className="space-y-1 overflow-hidden flex-1">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-primary/60">{s.field_name}</span>
                                <div className="text-sm font-medium p-2 bg-slate-50 rounded border border-slate-100 break-words">
                                    {typeof s.value_json === 'string' ? s.value_json : JSON.stringify(s.value_json)}
                                </div>
                            </div>
                            <div className="flex gap-2 shrink-0 ml-4">
                                <Button size="sm" variant="outline" className="h-9 w-9 p-0 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleReject(s.id)}>
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="default" className="h-9 w-9 p-0 bg-green-600 hover:bg-green-700" onClick={() => handleApprove(s.id)}>
                                    <Check className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground pt-1 border-t border-slate-50">
                            <Clock className="h-3 w-3" />
                            {t("editShop.submittedOn")} {new Date(s.created_at).toLocaleDateString()}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderMainForm = () => {
        if (mode === "address-only") {
            return (
                <div className="space-y-6 pt-4">
                    <div className="bg-primary/5 border border-primary/10 rounded-lg p-3 text-xs text-primary mb-4 flex items-start gap-2">
                        <Info className="h-4 w-4 shrink-0" />
                        <p>{t("editShop.addressOnlyDesc")}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>{t("editShop.streetNumber")}</Label>
                            <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Voorstraat 1" />
                        </div>
                        <div className="space-y-2">
                            <Label>{t("addShop.postalCodeLabel")}</Label>
                            <Input value={formData.postal_code} onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} placeholder="1234 AB" />
                        </div>
                        <div className="space-y-2">
                            <Label>{t("editShop.city")}</Label>
                            <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="Utrecht" />
                        </div>
                    </div>
                </div>
            );
        }

        const isCommunity = mode === "community-edit";

        return (
            <div className="space-y-6 pt-4">
                {!isCommunity && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>{t("editShop.name")}</Label>
                                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                            </div>
                            <div className="space-y-2">
                                <Label>{t("editShop.website")}</Label>
                                <Input value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>{t("editShop.aboutShop")}</Label>
                            <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={4} />
                        </div>

                        {canEditBanner ? (
                            <div className="space-y-3">
                                <Label>{t("editShop.bannerImage")}</Label>
                                {bannerImage ? (
                                    <div className="relative w-full h-32 rounded-lg overflow-hidden group border">
                                        <img src={bannerImage.url} className="w-full h-full object-cover" />
                                        <button onClick={removeBanner} className="absolute top-2 right-2 bg-white/90 p-1.5 rounded-full text-red-500 shadow-sm">
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <ImageCaptureInput
                                        onImage={(url) => {
                                            setPendingImages([url]);
                                            setCroppingIndex(0);
                                            setCroppingTarget("banner");
                                        }}
                                        label={t("editShop.setBanner")}
                                        className="w-full"
                                    />
                                )}
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <Label>{t("editShop.bannerImage")}</Label>
                                <p className="text-sm text-muted-foreground">
                                    {t("editShop.bannerVerifiedOnly")}
                                </p>
                            </div>
                        )}
                    </>
                )}

                <div className="space-y-4">
                    <Label className="flex items-center gap-2"><Clock className="h-4 w-4" /> {t("shopDetail.openingHours")}</Label>
                    <div className="space-y-2 border rounded-xl p-4 bg-slate-50/50">
                        {["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"].map((day, idx) => {
                            const sched = openingSchedule.find(s => s.weekday === idx) || { open: "09:00", close: "17:00", is_closed: true };
                            return (
                                <div key={day} className="flex items-center gap-4 text-sm">
                                    <div className="w-8 font-bold">{day}</div>
                                    <div className="flex items-center gap-3 flex-1">
                                        <Checkbox checked={!sched.is_closed} onCheckedChange={(c) => updateSchedule(idx, 'is_closed', !c)} />
                                        {!sched.is_closed ? (
                                            <div className="flex items-center gap-2">
                                                <Input type="time" className="h-8 w-20 text-xs" value={sched.open} onChange={(e) => updateSchedule(idx, 'open', e.target.value)} />
                                                <span>-</span>
                                                <Input type="time" className="h-8 w-20 text-xs" value={sched.close} onChange={(e) => updateSchedule(idx, 'close', e.target.value)} />
                                            </div>
                                        ) : <span className="text-xs text-muted-foreground">{t("common.closed")}</span>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="space-y-4">
                    <Label>{t("editShop.featuresPayment")}</Label>
                    <div className="grid grid-cols-2 gap-4 bg-slate-50/50 p-4 rounded-xl border">
                        {[
                            { id: "edit-charity", label: t("addShop.charity"), field: "has_charity" },
                            { id: "edit-horeca", label: t("addShop.horeca"), field: "has_horeca" },
                            { id: "edit-card", label: t("addShop.cardPayment"), field: "has_card_payment" },
                            { id: "edit-cash", label: t("addShop.cashPayment"), field: "has_cash_payment" }
                        ].map(item => (
                            <div key={item.id} className="flex items-center space-x-2">
                                <Checkbox id={item.id} checked={(formData as any)[item.field]} onCheckedChange={(v) => setFormData({ ...formData, [item.field]: !!v })} />
                                <Label htmlFor={item.id} className="text-sm font-medium cursor-pointer">{item.label}</Label>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="space-y-3">
                    <Label className="flex justify-between items-center">
                        <span>{t("editShop.shopPhotos", { count: images.length, max: maxImages })}</span>
                    </Label>
                    <div className="space-y-2">
                        <Label>{t("editShop.facadeFirst")}</Label>
                        {facadeImage ? (
                            <div className="relative w-40 h-28 rounded-xl overflow-hidden border shadow-sm group">
                                <img src={facadeImage.url} className="w-full h-full object-cover" />
                                <button onClick={() => setFacadeImage(null)} className="absolute top-1.5 right-1.5 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <X className="h-3 w-3" />
                                </button>
                            </div>
                        ) : (
                            <ImageCaptureInput
                                onImage={(url) => {
                                    setPendingImages([url]);
                                    setCroppingIndex(0);
                                    setCroppingTarget("facade");
                                }}
                                label={t("editShop.addFacadePhoto")}
                                className="w-full"
                            />
                        )}
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                        {images.map((img, idx) => (
                            <div key={idx} className="relative aspect-square group">
                                <img src={img.url} className="w-full h-full object-cover rounded-xl border shadow-sm" />
                                <button onClick={() => removeImage(idx)} className="absolute top-1.5 right-1.5 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <X className="h-3 w-3" />
                                </button>
                            </div>
                        ))}
                        {images.length < maxImages && (
                            <div className="aspect-square">
                                <ImageCaptureInput
                                    multiple
                                    onImages={(urls) => {
                                        setPendingImages(urls);
                                        setCroppingIndex(0);
                                        setCroppingTarget("gallery");
                                    }}
                                    label="+"
                                    className="w-full h-full text-2xl border-2 border-dashed rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-400"
                                />
                            </div>
                        )}
                    </div>

                    <ImageCropperModal
                        open={croppingIndex !== -1}
                        onOpenChange={(open) => !open && setCroppingIndex(-1)}
                        image={pendingImages[croppingIndex] || ""}
                        onCrop={(cropped) => {
                            if (croppingTarget === "banner") {
                                setBannerImage({ url: cropped, isNew: true });
                                setCroppingIndex(-1);
                                setPendingImages([]);
                            } else if (croppingTarget === "facade") {
                                setFacadeImage({ url: cropped, isNew: true });
                                setCroppingIndex(-1);
                                setPendingImages([]);
                            } else {
                                setImages((prev) => [...prev, { url: cropped, isNew: true }].slice(0, maxImages));
                                if (croppingIndex < pendingImages.length - 1) {
                                    setCroppingIndex(prev => prev + 1);
                                } else {
                                    setCroppingIndex(-1);
                                    setPendingImages([]);
                                }
                            }
                        }}
                    />
                </div>

                <div className="space-y-3">
                    <Label>{t("addShop.categories")}</Label>
                    <div className="flex flex-wrap gap-2">
                        {CATEGORIES.map(cat => (
                            <button
                                key={cat}
                                type="button"
                                onClick={() => toggleCategory(cat)}
                                className={`rounded-full h-8 px-3 text-[10px] md:text-xs flex items-center gap-2 border transition-all ${formData.categories.includes(cat)
                                    ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                    : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"
                                    }`}
                            >
                                <CategoryIcon category={cat} size={14} />
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto p-0 gap-0">
                <div className="p-6 pb-0">
                    <DialogHeader>
                        <DialogTitle className="text-xl">
                            {mode === "address-only" ? t("shopDetail.changeAddress") : (mode === "community-edit" ? t("shopDetail.improveInfo") : t("shopDetail.editShop"))}
                        </DialogTitle>
                        <DialogDescription>
                            {isOwnerOrAdmin
                                ? (shop.claimed_by_user_id === user?.id ? t("editShop.ownerDesc") : "Pas als beheerder (Admin) details van deze winkel aan.")
                                : t("editShop.communityDesc")}
                        </DialogDescription>
                    </DialogHeader>

                    {isOwnerOrAdmin ? (
                        <Tabs defaultValue="edit" className="w-full mt-6">
                            <TabsList className="grid w-64 grid-cols-2 h-10 bg-slate-100 p-1">
                                <TabsTrigger value="edit" className="rounded-md data-[state=active]:bg-white shadow-none">{t("common.edit")}</TabsTrigger>
                                <TabsTrigger value="suggestions" className="rounded-md data-[state=active]:bg-white shadow-none flex items-center gap-2 text-xs">
                                    {t("editShop.suggestions")}
                                    {suggestions && suggestions.length > 0 && (
                                        <span className="bg-primary text-primary-foreground w-4 h-4 flex items-center justify-center rounded-full text-[9px]">
                                            {suggestions.length}
                                        </span>
                                    )}
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="edit" className="pb-6">
                                {renderMainForm()}
                            </TabsContent>

                            <TabsContent value="suggestions" className="pb-6">
                                {renderSuggestions()}
                            </TabsContent>
                        </Tabs>
                    ) : (
                        <div className="pb-6">
                            {renderMainForm()}
                        </div>
                    )}
                </div>

                <div className="flex gap-3 p-6 bg-slate-50 border-t sticky bottom-0">
                    <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
                        {t("shops.cancel")}
                    </Button>
                    <Button
                        className="flex-1 gap-2 font-bold shadow-sm"
                        onClick={handleSave}
                        disabled={loading}
                    >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {mode === "address-only" ? t("editShop.submitSuggestion") : t("account.save")}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
